import {
  getDnaExportUrl,
  getDnaMapsUrl,
  getDnaNextSlotQueryKey,
  useDnaBox,
  useDnaNextSlot,
  useDnaOptions,
  useDnaPlace,
  useDnaQueue,
  useDnaRemove,
  useDnaSamples,
  useDnaUpdate,
} from "@api/generated/dna/dna";
import type { PlaceItemIn, QueuedPlot, Sample, SampleEditIn, Slot } from "@api/generated/model";
import { useUserMe } from "@api/generated/user/user";
import { useInvalidate } from "@app/queryClient";
import { useTrial } from "@app/trials";
import { hasRole, useSeasonFromLink } from "@app/workspace";
import { hint } from "@i18n";
import "@features/genetech/genetech.css";
import { LayoutLegend, LayoutMap, LayoutPager, slotText } from "@features/genetech/LayoutMap";
import { plotText } from "@features/genetech/text";
import { Autocomplete, Button, Checkbox, Loader, Modal, NumberInput, Select, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { ConfirmModal } from "@shell/Dialogs";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { formatDate, formatNumber, num } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { MissingList, type Requirement } from "@ui/RunBar";
import { Splitter } from "@ui/Splitter";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { createContext, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export const Route = createFileRoute("/lab/dna-isolation")({
  validateSearch: z.object({ kis: z.string().optional(), batch: z.string().optional() }),
  component: IsolationPage,
});

/** After samples were placed or deleted: the queue, the boxes and the batch's samples read again (not the choices). */
const DNA_CHANGED = /^\/api\/v1\/(dna|dilution)\//;
const useDnaChanged = () => useInvalidate(DNA_CHANGED);

/** Molekuláris labor → DNS-izolálás (the legacy frmDNSSource): a source trial's plots, one after another (or all at
 *  once), placed into the isolation batch at the next free position of the 10 × 10 boxes, which the server chooses,
 *  or one clicked on the box map. Literature data (no physical DNA, no quantity) fills the fictive boxes from 100 on,
 *  real DNA the ones below. */
function IsolationPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (p: Partial<typeof search>) => void navigate({ search: (s) => ({ ...s, ...p }), replace: true });
  const season = useSeasonFromLink(undefined);
  const me = useUserMe().data;
  const canEdit = hasRole(me?.roles, "labor");
  const options = useDnaOptions().data;
  const { trials } = useTrial(season, search.kis, undefined);
  const batch = search.batch?.trim() || null;
  const [draftBatch, setDraftBatch] = useState(search.batch ?? "");
  const [method, setMethod] = useState("CTAB");
  const [quantity, setQuantity] = useState<number | null>(100);
  const [literature, setLiterature] = useState(false);
  const [person, setPerson] = useState<string | null>(null);
  const contact = person ?? me?.full_name ?? "";
  const [skipped, setSkipped] = useState<number[]>([]);
  const [chosen, setChosen] = useState<number | null>(null); // the plot picked in the queue (else the next waiting)
  const [manual, setManual] = useState<Slot | null>(null); // a position clicked on the map (else the server's next)
  const [viewed, setViewed] = useState<number | null>(null);
  const queue = useDnaQueue(
    { ev: season ?? 0, kis: search.kis ?? "", batch: batch ?? undefined },
    { query: { enabled: !!season && !!search.kis } },
  ).data;
  const nextParams = { batch: batch ?? undefined, literature };
  const next = useDnaNextSlot(nextParams).data;
  const slot = manual ?? next ?? null;
  const boxNo = viewed ?? slot?.no ?? null;
  const box = useDnaBox(boxNo ?? 0, { query: { enabled: boxNo != null } }).data;
  const otherRange = !!box && box.literature !== literature; // a box of the other kind: its positions are not offered
  const queueColumns = useMemo<Column<QueuedPlot>[]>(
    () => [
      { id: "parc", header: t("dna.plot"), value: (p) => p.parc, text: (p) => String(p.parc), numeric: true, className: "mono" },
      {
        id: "name",
        header: t("dna.genotype"),
        value: (p) => p.name,
        cellClassName: (p) => (p.done || skipped.includes(p.parc) ? "mu" : undefined),
      },
      {
        id: "status",
        header: t("dna.status"),
        value: (p) => {
          const last = p.earlier.at(-1);
          if (p.done) return t("dna.done");
          if (skipped.includes(p.parc)) return t("dna.skipped");
          if (p.paz == null) return t("dna.noLine");
          return last ? t("dna.alreadyIsolated", { ev: last.year ?? "?" }) : null;
        },
        cell: (p) => <QueueStatus p={p} skipped={skipped.includes(p.parc)} />,
      },
    ],
    [t, skipped],
  );
  const queueTable = useDataTable("lab.isolation.queue", queueColumns, queue);
  // the next sample is the first waiting one in the order the person sees (filtered and sorted)
  const waiting = queueTable.rows.filter((p) => !p.done && !skipped.includes(p.parc) && p.paz != null);
  const current = waiting.find((p) => p.parc === chosen) ?? waiting[0];
  const client = useQueryClient();
  const changed = useDnaChanged();
  const place = useDnaPlace({
    mutation: {
      onSuccess: (out) => {
        const at = out.placed.at(-1)?.slot;
        notifications.show({ color: "green", message: t("dna.placed", { n: out.placed.length, position: at ? slotText(at) : "" }) });
        client.setQueryData(getDnaNextSlotQueryKey(nextParams), out.next);
        setManual(null);
        setViewed(null);
        changed();
      },
    },
  });
  const ready = canEdit && !!batch && (literature || !!quantity);
  const requirements = [
    { label: t("dna.needTarget"), done: !!batch, field: "dna-target" },
    ...(literature ? [] : [{ label: t("dna.needQuantity"), done: !!quantity, field: "dna-quantity" }]),
    { label: t("runBar.needsWriteAccess"), done: canEdit },
  ];
  const [placingAll, setPlacingAll] = useState(false);
  const placeItems = (items: PlaceItemIn[], onSettled?: () => void) =>
    batch &&
    (literature || quantity) &&
    place.mutate(
      { data: { batch, method: method || null, quantity: literature ? null : quantity, literature, person: contact || null, items } },
      { onSettled },
    );
  const setKind = (value: boolean) => {
    // the other kind continues in its own boxes: the position chosen and the box viewed are dropped
    setLiterature(value);
    setManual(null);
    setViewed(null);
  };
  if (season == null) return <Loader size="sm" m="md" />;
  return (
    <div className="page fill">
      <PageHeader
        title={t("dna.title")}
        subtitle={t("dna.subtitle")}
        season
        actions={
          batch && (
            <div className="hstack" style={{ gap: 8 }}>
              <SamplesButton batch={batch} canEdit={canEdit} />
              <a className="sm" href={getDnaMapsUrl({ batch })}>
                {t("dna.boxMap")}
              </a>
              <a className="sm" href={getDnaExportUrl({ batch })}>
                Excel
              </a>
            </div>
          )
        }
      />
      <div className="fill-row">
        <section className="card stack lab-queue" style={{ gap: 0 }}>
          <div className="lab-form">
            <Select
              id="dna-source"
              className="lab-wide"
              placeholder={hint("searchSelect")}
              size="xs"
              searchable
              label={t("dna.sourceTrial")}
              value={search.kis ?? null}
              onChange={(v) => set({ kis: v ?? undefined })}
              data={trials.map((o) => o.kis)}
            />
            <Autocomplete
              id="dna-target"
              className="lab-wide"
              size="xs"
              label={t("dna.target")}
              value={draftBatch}
              onChange={setDraftBatch}
              onOptionSubmit={(v) => set({ batch: v || undefined })}
              onBlur={() => set({ batch: draftBatch.trim() || undefined })}
              data={options?.batches ?? []}
            />
            <Autocomplete size="xs" label={t("dna.method")} value={method} onChange={setMethod} data={options?.methods ?? []} />
            <NumberInput
              id="dna-quantity"
              size="xs"
              label={t("dna.quantity")}
              allowDecimal={false}
              min={1}
              disabled={literature}
              placeholder={literature ? t("dna.noQuantity") : undefined}
              value={literature ? "" : (quantity ?? "")}
              onChange={(v) => setQuantity(num(v))}
            />
            <Checkbox
              className="lab-wide"
              size="xs"
              label={t("dna.literature")}
              description={t("dna.literatureHint")}
              checked={literature}
              onChange={(e) => setKind(e.currentTarget.checked)}
            />
            <Autocomplete
              size="xs"
              className="lab-wide"
              label={t("dna.contact")}
              value={contact}
              onChange={setPerson}
              data={options?.persons ?? []}
            />
          </div>
          <div className="hstack lab-queue-head">
            <span className="sec grow">{t("dna.queue", { n: waiting.length })}</span>
            {ready && waiting.length > 1 && (
              <Button size="compact-xs" variant="default" loading={place.isPending} onClick={() => setPlacingAll(true)}>
                {t("dna.placeAll", { n: waiting.length })}
              </Button>
            )}
          </div>
          <CurrentPlot.Provider value={current?.parc}>
            <DataTable
              table={queueTable}
              rowKey={(p) => p.parc}
              className="grow"
              rowProps={(p) => ({ className: p.parc === current?.parc ? "click sel" : "click", onClick: () => setChosen(p.parc) })}
              empty={!search.kis && t("dna.selectTrial")}
            />
          </CurrentPlot.Provider>
        </section>
        <Splitter cssVar="--lab-queue-w" side="start" min={260} max={640} />
        <section className="card pad stack grow" style={{ gap: 10, minWidth: 0 }}>
          <div className="hstack" style={{ gap: 8 }}>
            <LayoutPager no={boxNo} label={t("dna.box")} prev={t("dna.previousBox")} next={t("dna.nextBox")} onChange={setViewed} />
            {box && <span className="chip">{t("dna.occupied", { n: box.cells.length, size: box.rows.length * box.cols })}</span>}
            <span className="grow" />
            {canEdit && box && (
              <Button
                size="compact-sm"
                variant="default"
                onClick={() => {
                  const no = literature ? box.new_literature_box : box.new_box;
                  setManual({ no, row: box.rows.charAt(0), col: 1 });
                  setViewed(null);
                }}
              >
                {literature ? t("dna.newLiteratureBox") : t("dna.newBox")}
              </Button>
            )}
          </div>
          {box && (
            <span className={box.literature ? "chip y" : "chip"} style={{ alignSelf: "flex-start" }}>
              {box.literature ? t("dna.literatureBox") : t("dna.realBox")}
            </span>
          )}
          {otherRange && <p className="note warn sm">{literature ? t("dna.notLiteratureBox") : t("dna.notRealBox")}</p>}
          {box && (
            <LayoutMap
              no={box.no}
              rows={box.rows}
              cols={box.cols}
              next={slot}
              onPick={canEdit && !otherRange ? setManual : undefined}
              cells={box.cells.map((c) => ({
                row: c.row,
                col: c.col,
                text: String(c.e_parc ?? ""),
                title: `${c.kis ?? ""} · ${c.e_kis ?? ""} ${c.e_parc ?? ""} · ${c.name ?? ""}`,
                mine: c.kis === batch,
              }))}
            />
          )}
          <LayoutLegend mine={t("dna.legendBatch")} taken={t("dna.legendOther")} next={t("dna.legendNext")} free={t("dna.legendFree")} />
          <p className="sm mu">{t("dna.legend")}</p>
        </section>
        <aside className="card pad stack lab-side">
          <span className="sec">{t("dna.nextSample")}</span>
          {current ? (
            <NextSample
              key={current.parc}
              p={current}
              season={season}
              kis={search.kis ?? ""}
              slot={slot}
              ready={ready}
              requirements={requirements}
              placing={place.isPending}
              onSkip={() => setSkipped((s) => [...s, current.parc])}
              onPlace={(concentration, remark) =>
                placeItems([{ ev: season, kis: search.kis ?? "", parc: current.parc, slot: manual, concentration, remark }])
              }
            />
          ) : (
            <p className="sm mu">{search.kis ? t("dna.noMore") : t("dna.selectTrial")}</p>
          )}
        </aside>
      </div>
      {placingAll && (
        <ConfirmModal
          title={t("dna.placeAllTitle")}
          text={t("dna.placeAllConfirm", { n: waiting.length, batch: batch, position: slot ? slotText(slot) : "—" })}
          confirm={t("dna.placeAll", { n: waiting.length })}
          loading={place.isPending}
          onConfirm={() =>
            placeItems(
              waiting.map((p) => ({ ev: season, kis: search.kis ?? "", parc: p.parc })),
              () => setPlacingAll(false),
            )
          }
          onClose={() => setPlacingAll(false)}
        />
      )}
    </div>
  );
}

/** The plot the next placing takes. */
const CurrentPlot = createContext<number | undefined>(undefined);

function QueueStatus(props: { p: QueuedPlot; skipped: boolean }) {
  const { t } = useTranslation();
  const current = useContext(CurrentPlot) === props.p.parc;
  const { p } = props;
  const last = p.earlier.at(-1);
  return p.done ? (
    <span className="chip g">{t("dna.done")}</span>
  ) : props.skipped ? (
    <span className="chip">{t("dna.skipped")}</span>
  ) : p.paz == null ? (
    <span className="chip r">{t("dna.noLine")}</span>
  ) : last ? (
    <span className="chip y">{t("dna.alreadyIsolated", { ev: last.year ?? "?" })}</span>
  ) : current ? (
    <span className="chip y">{t("dna.nextChip")}</span>
  ) : null;
}

function NextSample(props: {
  p: QueuedPlot;
  season: number;
  kis: string;
  slot: Slot | null;
  ready: boolean;
  placing: boolean;
  onSkip: () => void;
  onPlace: (concentration: number | null, remark: string | null) => void;
  requirements: Requirement[];
}) {
  const { t } = useTranslation();
  const { p } = props;
  const [concentration, setConcentration] = useState<number | null>(null);
  const [remark, setRemark] = useState("");
  return (
    <form
      className="stack grow"
      style={{ gap: 10 }}
      onSubmit={(e) => {
        e.preventDefault();
        if (props.ready && !props.placing) props.onPlace(concentration, remark.trim() || null);
      }}
    >
      <b className="h2">{p.name ?? "—"}</b>
      <span className="sm mono mu">
        {props.season} {props.kis} {p.parc} · {p.paz} / {p.sid}
      </span>
      <div className="lab-box">
        <span className="sm mu">{t("dna.position")}</span>
        <b className="mono h2">{props.slot ? slotText(props.slot) : "—"}</b>
      </div>
      {p.earlier.length > 0 && (
        <div className="note warn sm">
          {t("dna.previouslyIsolated")}
          {p.earlier.map((x) => (
            <div key={x.id} className="mono">
              {x.year} · {x.kis} · {x.e_kis} {x.e_parc} · №{x.id}
            </div>
          ))}
        </div>
      )}
      <NumberInput
        size="xs"
        label={t("dna.concentration")}
        placeholder={t("dna.whenMeasured")}
        min={0}
        value={concentration ?? ""}
        onChange={(v) => setConcentration(num(v))}
      />
      <TextInput size="xs" label={t("dna.remark")} value={remark} onChange={(e) => setRemark(e.currentTarget.value)} />
      <span className="grow" />
      <MissingList requirements={props.requirements} />
      <Button type="button" variant="default" onClick={props.onSkip}>
        {t("dna.skip")}
      </Button>
      <Button type="submit" disabled={!props.ready} loading={props.placing}>
        {t("dna.place", { position: props.slot ? `${props.slot.row} ${props.slot.col}` : "" })}
      </Button>
    </form>
  );
}

/** The batch's samples: position, plot, line, isolated, quantity; one deleted when nothing names it. */
function SamplesButton({ batch, canEdit }: { batch: string; canEdit: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Sample | null>(null);
  const samples = useDnaSamples({ batch }, { query: { enabled: open } }).data;
  const [deleting, setDeleting] = useState<Sample | null>(null);
  const columns = useMemo<Column<Sample>[]>(
    () => [
      { id: "id", header: "ID", value: (s) => s.id, text: (s) => String(s.id), numeric: true, className: "mono" },
      { id: "slot", header: t("dna.position"), value: (s) => (s.slot ? slotText(s.slot) : null), className: "mono" },
      { id: "plot", header: t("dna.plot"), value: (s) => plotText(s), className: "mono sm" },
      { id: "name", header: t("dna.genotype"), value: (s) => s.name },
      { id: "isolated", header: t("dna.isolated"), value: (s) => s.isolated, text: (s) => formatDate(s.isolated) },
      {
        id: "quantity",
        header: "µl",
        value: (s) => s.quantity,
        text: (s) => (s.quantity == null ? t("dna.literatureShort") : String(s.quantity)),
        numeric: true,
      },
      { id: "concentration", header: "ng/µl", value: (s) => s.concentration, cell: (s) => formatNumber(s.concentration, 1), numeric: true },
      ...(canEdit
        ? [
            {
              id: "edit",
              header: "",
              name: t("parity.edit"),
              fixed: true,
              cell: (s) => (
                <>
                  <Button size="compact-xs" variant="subtle" onClick={() => setEditing(s)}>
                    {t("parity.edit")}
                  </Button>
                  <button type="button" className="chip" aria-label={t("dna.deleteSample", { id: s.id })} onClick={() => setDeleting(s)}>
                    ✕
                  </button>
                </>
              ),
            } as Column<Sample>,
          ]
        : []),
    ],
    [t, canEdit],
  );
  const table = useDataTable("lab.isolation.samples", columns, samples);
  const changed = useDnaChanged();
  const remove = useDnaRemove({
    mutation: {
      onSuccess: () => {
        changed();
        setDeleting(null);
        notifications.show({ color: "green", message: t("dna.deleted") });
      },
    },
  });
  return (
    <>
      <Button size="compact-sm" variant="default" onClick={() => setOpen(true)}>
        {t("dna.samples")}
      </Button>
      {editing && <SampleEditor key={editing.id} sample={editing} onClose={() => setEditing(null)} />}
      {deleting && (
        <ConfirmModal
          title={t("dna.deleteTitle")}
          text={t("dna.deleteConfirm", { position: deleting.slot ? slotText(deleting.slot) : "—", name: deleting.name ?? "—" })}
          confirm={t("dna.deleteButton")}
          danger
          loading={remove.isPending}
          onConfirm={() => remove.mutate({ id: deleting.id })}
          onClose={() => setDeleting(null)}
        />
      )}
      <Modal opened={open} onClose={() => setOpen(false)} title={t("dna.samplesTitle", { batch: batch })} size="xl">
        {!samples ? (
          <Loader size="sm" />
        ) : (
          <div className="stack" style={{ maxHeight: 520 }}>
            <DataTable table={table} rowKey={(s) => s.id} className="grow" />
          </div>
        )}
      </Modal>
    </>
  );
}

function SampleEditor({ sample, onClose }: { sample: Sample; onClose: () => void }) {
  const { t } = useTranslation();
  const changed = useDnaChanged();
  const [form, setForm] = useState<SampleEditIn>({
    batch: sample.kis ?? "",
    method: sample.method,
    quantity: sample.quantity,
    concentration: sample.concentration,
    person: sample.person,
    remark: sample.remark,
    token: sample.token,
  });
  const set = (p: Partial<SampleEditIn>) => setForm((f) => ({ ...f, ...p }));
  const [literature, setLiterature] = useState(sample.quantity == null); // no quantity: literature data, no DNA
  const save = useDnaUpdate({
    mutation: {
      onSuccess: () => {
        changed();
        onClose();
      },
    },
  });
  return (
    <Modal opened onClose={onClose} title={t("parity.sample")}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate({ id: sample.id, data: { ...form, quantity: literature ? null : form.quantity } });
        }}
      >
        <TextInput label={t("dna.target")} required value={form.batch} onChange={(e) => set({ batch: e.currentTarget.value })} />
        <TextInput label={t("dna.method")} value={form.method ?? ""} onChange={(e) => set({ method: e.currentTarget.value })} />
        <TextInput label={t("dna.contact")} value={form.person ?? ""} onChange={(e) => set({ person: e.currentTarget.value })} />
        <Checkbox
          label={t("dna.literature")}
          description={t("dna.literatureEditHint")}
          checked={literature}
          onChange={(e) => setLiterature(e.currentTarget.checked)}
        />
        <NumberInput
          label={t("dna.quantity")}
          min={1}
          allowDecimal={false}
          disabled={literature}
          placeholder={literature ? t("dna.noQuantity") : undefined}
          value={literature ? "" : (form.quantity ?? "")}
          onChange={(v) => set({ quantity: num(v) })}
        />
        <NumberInput
          label={t("dna.concentration")}
          min={0}
          value={form.concentration ?? ""}
          onChange={(v) => set({ concentration: num(v) })}
        />
        <TextInput label={t("dna.remark")} value={form.remark ?? ""} onChange={(e) => set({ remark: e.currentTarget.value })} />
        <Button type="submit" loading={save.isPending} disabled={!form.batch.trim() || (!literature && !form.quantity)}>
          {t("parity.save")}
        </Button>
      </form>
    </Modal>
  );
}
